import type { TourStep } from './tourTypes';
import { useTourStore } from '@/stores/tourStore';
import { usePostsStore } from '@/stores/postsStore';
import { useWorkspaceStore } from '@/stores/workspaceStore';
import { openMobileMenuForTour } from './tourMobileMenu';
import { requestTourReflow } from './tourReflow';
import type { TourPhase } from '@/stores/tourStore';

/**
 * ============================================================================
 * GUIDED TOUR — STEP CONTENT (Phase 3a)
 * ============================================================================
 * The onboarding and tool tours are each multi-stage: their most important
 * elements only mount AFTER a user action. So each tour is split into a
 * pre/post pair of step arrays:
 *
 *   - `onboardingPreAnalyzeSteps`  — welcome + company URL + analyze button
 *   - `onboardingPostAnalyzeSteps` — editable company fields + save (re-fires
 *                                    automatically once Analyze completes)
 *   - `getToolIntroPreGenSteps()`  — workspace, prompt, selectors, generate
 *   - `getToolIntroPostGenSteps()` — variations, psychology, save, posting
 *                                    (re-fires after the first generation)
 *
 * Steps target real DOM elements via the `data-tour` attribute convention
 * (see tourStore.ts). A step with NO `element` renders as a centered,
 * modal-style popover — used for the welcome / wrap-up steps.
 *
 * MISSING ELEMENTS: TourProvider filters out any step whose `element` selector
 * does not currently resolve in the DOM, so steps pointing at elements that
 * are not yet mounted are skipped silently rather than crashing driver.js.
 *
 * TIER GATING: `getToolIntroPreGenSteps(tier)` returns the pre-gen steps with
 * service-selector and image-mode copy swapped for an upsell message on the
 * Basic tier. The steps are NEVER skipped — Basic users still see the feature
 * so they know it exists (soft upsell). The post-gen steps are tier-agnostic.
 * ============================================================================
 */

export type SubscriptionTier = 'basic' | 'basic_plus' | 'pro';

/** Buttons shown on every step. `close` routes through CloseConfirmModal. */
const TOUR_BUTTONS: ('next' | 'previous' | 'close')[] = ['next', 'previous', 'close'];

/**
 * The page a phase lives on. Used when the user detours mid-tour (e.g. "Create
 * new workspace") — the tour pauses and offers a one-click way back to where it
 * left off, instead of silently vanishing.
 */
export function phaseHomeRoute(phase: TourPhase): string | null {
  if (!phase) return null;
  if (phase.startsWith('onboarding')) return '/onboarding';
  if (phase.startsWith('tool_')) return '/tool'; // tool_orientation + tool_intro*
  return DEEP_DIVE_CHAIN_ORDER.find((e) => e.phase === phase)?.route ?? null;
}

// ---------------------------------------------------------------------------
// Hands-on gates for the tool tour. These make the first run a real run: the
// tour won't advance past these steps until the user has actually done them.
// "skip tour" always remains available, so nobody gets trapped.
// ---------------------------------------------------------------------------

/**
 * The compose state (prompt / services / platforms / results) is kept PER
 * WORKSPACE and mirrored into `_cachedWorkspaceData` — the root fields are only
 * initial defaults and never update. Read the cached workspace data first, and
 * fall back to the root so this can't break if that internal shape changes.
 */
function composeState() {
  const s = usePostsStore.getState() as unknown as Record<string, unknown>;
  const cached = (s._cachedWorkspaceData ?? {}) as Record<string, unknown>;
  const pick = <T,>(key: string, empty: T): T => {
    const c = cached[key] as T | undefined;
    const r = s[key] as T | undefined;
    if (Array.isArray(c) && c.length > 0) return c;
    if (typeof c === 'string' && c.length > 0) return c;
    if (c && typeof c === 'object' && Object.keys(c).length > 0) return c;
    return (r ?? c ?? empty) as T;
  };
  return {
    prompt: pick<string>('prompt', ''),
    selectedServices: pick<string[]>('selectedServices', []),
    selectedPlatforms: pick<string[]>('selectedPlatforms', []),
    generatedData: pick<Record<string, unknown>>('generatedData', {}),
  };
}

/** At least one platform selected. */
const ACTION_PICK_PLATFORM = {
  done: () => composeState().selectedPlatforms.length > 0,
  hint: 'pick at least one platform',
};

/**
 * A real service chosen (not the default "general"). If the workspace has no
 * services at all there's nothing to pick, so the gate passes automatically.
 */
const ACTION_PICK_SERVICE = {
  done: () => {
    const ws = useWorkspaceStore.getState().activeWorkspace;
    // Workspace still loading — stay gated. Passing here would latch the gate
    // "already satisfied" before the services have even arrived.
    if (!ws) return false;
    if ((ws.services?.length ?? 0) === 0) return true; // genuinely nothing to pick
    return composeState().selectedServices.some((s) => s !== 'general');
  },
  hint: 'choose one of your services',
};

/** Something typed into the prompt box. */
const ACTION_WRITE_PROMPT = {
  done: () => composeState().prompt.trim().length > 0,
  hint: 'type your post idea',
};

/** Posts actually generated. */
const ACTION_GENERATE = {
  done: () => Object.keys(composeState().generatedData).length > 0,
  hint: 'hit generate to write your posts',
};

/**
 * A gated WALK to another page: the step spotlights the real nav control and
 * waits for the user to click it themselves. Satisfied once they land on
 * `route`. Pair it with `handOff(nextPhase)` on the popover — `handOff` does NOT
 * navigate, because by the time it runs the user already has.
 *
 * Only ever gate a control that is visible at EVERY breakpoint. The settings
 * sidebar is `hidden lg:block`, so gating a click on it would trap a mobile user
 * behind "skip tour" (the dim blocks everything outside the cutout). Those hops
 * teleport via `chainTo` instead. The controls we do gate on — the UserMenu, the
 * workspace switcher, Feed's "write your own" — are always on screen.
 */
const walkTo = (route: string, hint: string) => ({
  done: () => window.location.pathname.startsWith(route),
  hint,
});

/**
 * Onboarding: a region actually chosen. The form's own Continue button is
 * disabled until then, so its enabled-ness IS the source of truth.
 */
const ACTION_PICK_REGION = {
  done: () => {
    const card = document.querySelector('[data-tour="onboarding-region-card"]');
    if (!card) return false;
    const cont = Array.from(card.querySelectorAll('button')).find((b) =>
      /continue/i.test(b.textContent || ''),
    ) as HTMLButtonElement | undefined;
    return !!cont && !cont.disabled;
  },
  hint: 'pick your region',
};

/**
 * Onboarding: at least one service on the list. Usually pre-filled from the
 * website analysis, in which case this passes immediately and just explains —
 * but if we found none, it directs the user to add one.
 */
const ACTION_HAS_SERVICE = {
  done: () => {
    const card = document.querySelector('[data-tour="onboarding-services-card"]');
    if (!card) return false;
    // "N/10 services added" is rendered on this screen — the source of truth.
    const m = (card.textContent || '').match(/(\d+)\s*\/\s*10\s*services added/i);
    return m ? parseInt(m[1], 10) > 0 : true;
  },
  hint: 'add at least one service',
};


// ===========================================================================
// Onboarding tour — the company-analysis form (/onboarding)
// ===========================================================================
//
// The form is multi-stage: the URL-input stage renders first, and the editable
// company fields only appear AFTER "Analyze" runs. The tour therefore splits in
// two — the pre-analyze steps run on page mount, then `Onboarding.tsx` re-fires
// the post-analyze steps automatically once the confirm stage is mounted.

/** Pre-analyze: shown on page mount, ends at the analyze button. */
export const onboardingPreAnalyzeSteps: TourStep[] = [
  {
    popover: {
      title: "let's set up your workspace 🚀",
      description:
        'first we teach zupa your company, so every post sounds like you — not generic ai. just a few quick steps. start below.',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    // ONE section over the whole input form — the two optional fields are
    // explained as callouts (badged optional), never forced into their own
    // step, so "next" no longer skips straight past them to Analyze.
    target: '[data-tour="onboarding-form"]',
    // Hands-on: the user must actually analyze their company to move on — this
    // is the step that creates their workspace.
    action: {
      done: () => document.querySelector('[data-tour="onboarding-confirm-fields"]') !== null,
      hint: 'paste your url and hit Analyze My Company',
    },
    callouts: [
      {
        target: '[data-tour="onboarding-company-url"]',
        title: 'start here',
        description:
          'paste your website link — zupa reads your site and auto-fills industry, brand voice, and audience.',
      },
      {
        target: '[data-tour="onboarding-content-url"]',
        title: 'only if needed',
        description: 'point us at a specific page if your homepage is thin on company info.',
        optional: true,
      },
      {
        target: '[data-tour="onboarding-about"]',
        title: 'add context',
        description: "a few words about your company — helps most when your site is JS-heavy and hard to read.",
        optional: true,
      },
      {
        target: '[data-tour="onboarding-analyze-button"]',
        title: 'then analyze',
        description: 'when your url is in, hit analyze — zupa fills everything out for you.',
      },
    ],
    popover: {
      title: 'teach zupa your company',
      description:
        "everything on this screen is one step. drop your url, add the optional bits if they help, then hit analyze — we'll pick the tour back up once it's done.",
      side: 'top',
      align: 'center',
      showButtons: TOUR_BUTTONS,
      nextBtnText: 'got it',
      // End of the pre-analyze tour. endTour(false): reset tour state WITHOUT
      // persisting completion, so the post-analyze tour can re-fire once the
      // confirm stage mounts. lastCompletedPhase ('onboarding') is recorded by
      // endTour — Onboarding.tsx reads it to gate the post-analyze trigger.
      onNextClick: () => {
        void useTourStore.getState().endTour(false);
      },
    },
  },
];

/**
 * Post-analyze: re-fired automatically once the editable fields are mounted.
 *
 * NOTE: the confirm card is an internal scroll container (max-h-[80vh]
 * overflow-y-auto), so it's toured ONE FIELD AT A TIME — each field is scrolled
 * into view and spotlit on its own. A whole-section highlight would ring content
 * that the card clips as you scroll, drifting the ring off the visible area.
 * (The input-form section is fine because that card does NOT scroll.)
 */
export const onboardingPostAnalyzeSteps: TourStep[] = [
  {
    element: '[data-tour="onboarding-company-name"]',
    popover: {
      title: 'your company name',
      description: 'check the name here and tweak it if zupa pulled it wrong.',
      side: 'right',
      align: 'center',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    element: '[data-tour="onboarding-industry"]',
    popover: {
      title: 'your industry',
      description: 'confirm your industry — it shapes the angle of every post. edit it any time.',
      side: 'right',
      align: 'center',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    element: '[data-tour="onboarding-description"]',
    popover: {
      title: 'what you do',
      description: 'edit your company description — zupa leans on it for context on every post.',
      side: 'right',
      align: 'center',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    element: '[data-tour="onboarding-audience"]',
    popover: {
      title: 'who you talk to',
      description: 'set your target audience — posts get written to land with these people.',
      side: 'right',
      align: 'center',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    element: '[data-tour="onboarding-brand-voice"]',
    popover: {
      title: 'your brand voice',
      description: 'set the tone zupa writes in — professional, friendly, bold, whatever fits.',
      side: 'right',
      align: 'center',
      showButtons: TOUR_BUTTONS,
    },
  },
  // ---- Visual Brand DNA ----------------------------------------------------
  // Toured FIELD BY FIELD, like the rest of this scroll-container card. It used
  // to be a single step anchored on the section's HEADING row, so the ring wrapped
  // the title and the badge and nothing else — the logo, the colours, the style
  // and the photography all sat below it in the dark, unexplained, and the tour
  // appeared to just stop there.
  //
  // The whole section is optional (only rendered when the site actually yielded
  // brand data). If it's absent the DOM guard drops every one of these and
  // brand-voice becomes the last step — the overlay's endTour(false) fallback then
  // ends the phase cleanly, so the chain still holds.
  {
    element: '[data-tour="onboarding-brand-dna"]',
    popover: {
      title: 'your visual brand 🎨',
      description:
        'zupa read your site and pulled out your visual identity — logo, colours, style, mood. it uses this so the images it generates actually look like they came from you. have a look down the section.',
      side: 'right',
      align: 'center',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    element: '[data-tour="onboarding-brand-logo"]',
    popover: {
      title: 'your logo',
      description: 'we grabbed it from your site — swap it for a better one, or upload it if we missed it.',
      side: 'right',
      align: 'center',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    element: '[data-tour="onboarding-brand-colors"]',
    popover: {
      title: 'your brand colours',
      description:
        'the first one is your primary. click a swatch to correct it, × to drop it, or add one we missed — these end up in your generated images, so it’s worth a second look.',
      side: 'right',
      align: 'center',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    element: '[data-tour="onboarding-brand-style"]',
    popover: {
      title: 'style and mood',
      description:
        'the feel of your imagery — how it looks, and how it should land. tweak the wording and every ai image follows it.',
      side: 'right',
      align: 'center',
      showButtons: TOUR_BUTTONS,
      nextBtnText: 'got it',
      // Natural end of the post-analyze tour. endTour(false): still NOT
      // persisting completion — the rest of the wizard, and the /tool tour, follow.
      onNextClick: () => {
        void useTourStore.getState().endTour(false);
      },
    },
  },
];

// ===========================================================================
// Onboarding wizard — one beat per stage AFTER the confirm card
// ===========================================================================
//
// The onboarding wizard is a linear sequence: input → confirm → region →
// services → voice → past-posts → fact-bank → personas. Each stage below gets a
// single tour beat that fires when the stage mounts (chained by the sequence in
// Onboarding.tsx). Each ends with endTour(false) so completion isn't persisted
// mid-flow — the chain (and eventually the /tool tour) must still run.

const endOnboardingBeat = () => {
  void useTourStore.getState().endTour(false);
};

// Each post-confirm stage highlights its WHOLE card (heading + every field), so
// nothing on the screen is left unexplained. These cards don't scroll
// internally, so a full-card spotlight is safe (unlike the confirm card).

/** Region / SEO targeting stage. */
export const onboardingRegionSteps: TourStep[] = [
  {
    element: '[data-tour="onboarding-region-card"]',
    action: ACTION_PICK_REGION,
    popover: {
      title: 'where you sell',
      description:
        'pick your region here — it sets the seo keywords zupa suggests, so posts target the market you actually sell into.',
      side: 'left',
      align: 'center',
      showButtons: TOUR_BUTTONS,
      nextBtnText: 'got it',
      onNextClick: endOnboardingBeat,
    },
  },
];

/** Services / products stage. */
export const onboardingServicesSteps: TourStep[] = [
  {
    element: '[data-tour="onboarding-services-card"]',
    action: ACTION_HAS_SERVICE,
    popover: {
      title: 'your services',
      description:
        'these are the things you sell — we pre-filled what we found. add or remove any. tag a post to a service and zupa writes about that exact offering, not your company in general.',
      side: 'left',
      align: 'center',
      showButtons: TOUR_BUTTONS,
      nextBtnText: 'got it',
      onNextClick: endOnboardingBeat,
    },
  },
];

/** Company voice — tones + writing samples stage. */
export const onboardingVoiceSteps: TourStep[] = [
  {
    element: '[data-tour="onboarding-voice-card"]',
    popover: {
      title: 'sound like you',
      description:
        'pick 1–2 tones up top, then paste 3 writing samples below. the samples are the single biggest thing that makes generated posts match your real voice.',
      side: 'left',
      align: 'center',
      showButtons: TOUR_BUTTONS,
      nextBtnText: 'got it',
      onNextClick: endOnboardingBeat,
    },
  },
];

/** Past posts (optional voice examples) stage. */
export const onboardingExamplesSteps: TourStep[] = [
  {
    element: '[data-tour="onboarding-examples-card"]',
    popover: {
      title: 'past posts',
      description:
        'optional — paste posts you’ve already written so zupa learns your style from real examples. add as many as you like, or skip.',
      side: 'left',
      align: 'center',
      showButtons: TOUR_BUTTONS,
      nextBtnText: 'got it',
      onNextClick: endOnboardingBeat,
    },
  },
];

/** Fact bank starter stage. */
export const onboardingFactsSteps: TourStep[] = [
  {
    element: '[data-tour="onboarding-facts-card"]',
    popover: {
      title: 'your facts & achievements',
      description:
        'optional — a few true facts (a number you’re proud of, a client win, a credential) that zupa can weave into posts for credibility.',
      side: 'left',
      align: 'center',
      showButtons: TOUR_BUTTONS,
      nextBtnText: 'got it',
      onNextClick: endOnboardingBeat,
    },
  },
];

// ===========================================================================
// Tool orientation — the first landing on /tool, straight out of onboarding
// ===========================================================================
//
// Onboarding's "Finish Setup" navigates the user to /tool, so this is where
// they physically are. But the tool is the FINALE, not the next chapter: we
// name the room, promise to come back to it, and send them off on the lap
// around the app. They walk to workspace settings themselves.

export const toolOrientationSteps: TourStep[] = [
  {
    popover: {
      title: "you're in ✨",
      description:
        "this is the studio — where every post gets written. we'll come back and make your first one in a few minutes. first, a quick lap so you know where everything lives.",
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    element: '[data-tour="workspace-switcher"]',
    action: walkTo('/settings/workspaces', 'open the menu → “Manage Workspaces”'),
    popover: {
      title: 'start with your workspace',
      description:
        'everything you just told zupa — your profile, services, voice, audience — lives behind this menu. open it and pick “manage workspaces”.',
      side: 'bottom',
      align: 'start',
      showButtons: TOUR_BUTTONS,
      onNextClick: () => handOff('deep_dive_workspaces_page'),
    },
  },
];

// ===========================================================================
// Tool intro tour — main generation page (/tool). THE FINALE.
// ===========================================================================

/**
 * Reveal the composer's "More options" section so its controls exist before the
 * tour spotlights them. Carousel / Format / Campaign Intent live behind that
 * disclosure now — without this the DOM guard would drop all three steps and the
 * tour would silently stop mentioning half the generation controls.
 *
 * Paired with `lateMount: true` on those steps, since the guard runs once at
 * phase start, before this has had a chance to fire.
 */
function openMoreOptionsForTour(): void {
  // Already open (the disclosure force-opens when any advanced option is set).
  if (document.querySelector('[data-tour="carousel-selector"]')) return;
  const toggle = document.querySelector('[data-tour="more-options"]') as HTMLElement | null;
  toggle?.click();
  setTimeout(() => requestTourReflow(), 60);
}

/** Copy for the service-selector step, by tier. */
const SERVICE_SELECTOR_COPY: Record<SubscriptionTier, string> = {
  pro: "tag a service or product here — posts get written about that exact offering. leave it on general posts for broad content.",
  basic_plus:
    "tag a service or product here — posts get written about that exact offering. leave it on general posts for broad content.",
  basic:
    "🔒 pro feature — tag a post to a specific service or product so it's about that exact offering. upgrade to unlock service-specific posts.",
};

/**
 * Copy for the image-mode step, by tier.
 *
 * This carries the whole image story on purpose. The post-gen chapter has
 * dedicated steps for the generated image, its on-image text editor, and
 * copy/download — but those are DOM-guarded and vanish for anyone who generated
 * text-only, which is the default. So the one step everybody definitely sees has
 * to say what images can do, not just that the dropdown exists.
 */
const IMAGE_MODE_COPY: Record<SubscriptionTier, string> = {
  pro: 'choose how images get made: let ai draw one from your brand dna (your colours, your style, with a text hook written onto it), pick a stock suggestion, upload your own — or none. pick ai and you can regenerate it, edit the text on it, and drag it around after.',
  basic_plus:
    'choose how images get made: let ai draw one from your brand dna (your colours, your style, with a text hook written onto it), pick a stock suggestion, upload your own — or none. pick ai and you can regenerate it, edit the text on it, and drag it around after.',
  basic:
    '🔒 stock suggestions and ai-generated images are paid features. on your plan posts go out text-only — upgrade and zupa draws on-brand images and lets you edit the text right on them.',
};

/**
 * Pre-generation tool steps for a given tier. Covers the workspace and the
 * generation inputs, ending at the generate button. Tier only changes the copy
 * on the service-selector and image-mode steps — no step is ever removed.
 *
 * The platform / service / prompt / generate steps are HANDS-ON: the user has
 * to actually do them, so the first pass through the tool is a complete run
 * ending in real posts.
 */
export function getToolIntroPreGenSteps(tier: SubscriptionTier): TourStep[] {
  return [
    {
      popover: {
        title: "now — your first post 🚀",
        description:
          "you've seen the whole app. this is the part it was all for: you bring an idea, zupa writes the posts. let's do it for real.",
        showButtons: TOUR_BUTTONS,
      },
    },
    {
      element: '[data-tour="view-toggle"]',
      // On mobile the header copy is `hidden lg:flex` and the real toggle lives in
      // the side pane, so open it. `lateMount` keeps the step alive through the DOM
      // guard, which runs before this fires; `queryVisible` then picks whichever
      // copy is actually on screen.
      lateMount: true,
      onHighlightStarted: () => {
        if (!openMobileMenuForTour()) return;
        setTimeout(() => requestTourReflow(), 60);
      },
      popover: {
        title: 'two ways to create',
        description:
          'zupapost writes short social posts; case study turns a client win into a long-form story. this tour covers zupapost — the everyday one.',
        side: 'bottom',
        align: 'center',
        showButtons: TOUR_BUTTONS,
      },
    },
    {
      // Points at the composer's "Post to" chips, not the sidebar — platform
      // selection moved into the compose card, so there's no mobile-menu dance
      // needed any more: the chips are always on screen.
      element: '[data-tour="platform-picker"]',
      action: ACTION_PICK_PLATFORM,
      popover: {
        title: 'choose your platforms',
        description:
          'go ahead — tap a platform. each one gets copy tuned to its own algorithm, and you need at least one to generate.',
        side: 'bottom',
        align: 'start',
        showButtons: TOUR_BUTTONS,
      },
    },
    {
      element: '[data-tour="prompt-box"]',
      action: ACTION_WRITE_PROMPT,
      popover: {
        title: 'start with an idea',
        description:
          'type your post idea here — a thought, a rough draft, even a single word. zupa expands it into full posts. give it a go.',
        side: 'bottom',
        align: 'center',
        showButtons: TOUR_BUTTONS,
      },
    },
    {
      element: '[data-tour="my-prompts"]',
      popover: {
        title: 'reuse a good prompt',
        description:
          'found an angle that works? save the prompt here and pull it back out any time — your own reusable library, so you never retype a winner.',
        side: 'right',
        align: 'center',
        showButtons: TOUR_BUTTONS,
      },
    },
    {
      element: '[data-tour="service-selector"]',
      action: ACTION_PICK_SERVICE,
      popover: {
        title: 'tag a service',
        description: SERVICE_SELECTOR_COPY[tier],
        side: 'bottom',
        align: 'start',
        showButtons: TOUR_BUTTONS,
      },
    },
    {
      element: '[data-tour="persona-selector"]',
      popover: {
        title: 'pick a persona',
        description:
          'pick who you’re talking to here — zupa shifts the tone to match that audience.',
        side: 'bottom',
        align: 'start',
        showButtons: TOUR_BUTTONS,
      },
    },
    {
      element: '[data-tour="image-mode"]',
      popover: {
        title: 'add images',
        description: IMAGE_MODE_COPY[tier],
        side: 'bottom',
        align: 'start',
        showButtons: TOUR_BUTTONS,
      },
    },
    {
      element: '[data-tour="carousel-selector"]',
      // Lives behind the composer's "More options" disclosure.
      lateMount: true,
      onHighlightStarted: openMoreOptionsForTour,
      popover: {
        title: 'multi-slide carousels',
        description:
          'want a carousel instead of a single image? build one from your own images or let ai generate the slides.',
        side: 'bottom',
        align: 'center',
        showButtons: TOUR_BUTTONS,
      },
    },
    {
      element: '[data-tour="format-selector"]',
      // Lives behind the composer's "More options" disclosure.
      lateMount: true,
      onHighlightStarted: openMoreOptionsForTour,
      popover: {
        title: 'caption or caption + carousel',
        description:
          'keep it a plain caption, or add a 5–7 slide text carousel alongside it (great for instagram).',
        side: 'bottom',
        align: 'center',
        showButtons: TOUR_BUTTONS,
      },
    },
    {
      element: '[data-tour="campaign-intent"]',
      // Lives behind the composer's "More options" disclosure.
      lateMount: true,
      onHighlightStarted: openMoreOptionsForTour,
      popover: {
        title: 'set the angle (optional)',
        description:
          'nudge the whole batch toward a goal — a launch, a promo, thought leadership — or leave it on none for everyday posts.',
        side: 'bottom',
        align: 'end',
        showButtons: TOUR_BUTTONS,
      },
    },
    {
      element: '[data-tour="generate-button"]',
      action: ACTION_GENERATE,
      popover: {
        title: 'hit generate',
        description:
          "now hit generate — zupa picks the psychology principles and writes every post. the tour picks back up the moment your posts land.",
        side: 'top',
        align: 'center',
        showButtons: TOUR_BUTTONS,
        nextBtnText: 'got it',
        // End of the pre-gen tour. endTour(false): reset state WITHOUT
        // persisting completion so the post-gen tour can re-fire once the
        // generated variations are in the DOM. lastCompletedPhase
        // ('tool_intro') is recorded for the post-gen trigger gate.
        onNextClick: () => {
          void useTourStore.getState().endTour(false);
        },
      },
    },
  ];
}

/**
 * Post-generation tool steps. Re-fired automatically once the first generation
 * completes and the variation cards are in the DOM. Tier-agnostic — every
 * feature shown here (variations, psychology, save, posting) exists on all
 * tiers; only the variation COUNT differs, which the copy keeps generic.
 */
export function getToolIntroPostGenSteps(): TourStep[] {
  return [
    {
      element: '[data-tour="post-variations"]',
      popover: {
        title: 'three posts, not one',
        description:
          'you got up to 3 variations per platform, each built on a DIFFERENT psychological angle — a bold claim, a question hook, a story. click between them and pick the one that sounds like you.',
        side: 'top',
        align: 'center',
        showButtons: TOUR_BUTTONS,
      },
    },
    {
      element: '[data-tour="psychology-card"]',
      popover: {
        title: 'why it works',
        description:
          'open this card — it shows the exact psychology zupa used and why this post will land. this is the part other tools can’t explain.',
        side: 'bottom',
        align: 'center',
        showButtons: TOUR_BUTTONS,
      },
    },
    {
      // Only rendered once there's an image/carousel to preview — DOM-guarded
      // out for a text-only post, like every image step below.
      element: '[data-tour="post-preview"]',
      popover: {
        title: 'see it before you post it',
        description:
          'hit the eye to preview the post exactly as it will look in the real feed — image, caption and all. no nasty surprises after you publish.',
        side: 'right',
        align: 'center',
        showButtons: TOUR_BUTTONS,
      },
    },
    {
      element: '[data-tour="post-copy"]',
      popover: {
        title: 'copy the text',
        description:
          'grab the caption to your clipboard with one click — hashtags included — if you’d rather paste it somewhere yourself.',
        side: 'left',
        align: 'center',
        showButtons: TOUR_BUTTONS,
      },
    },
    {
      element: '[data-tour="post-image"]',
      popover: {
        title: 'your generated image',
        description:
          'if you chose an ai image, zupa drew it from your brand dna — your colours, your style — and wrote a text hook onto it. don’t like it? regenerate for a fresh one.',
        side: 'right',
        align: 'center',
        showButtons: TOUR_BUTTONS,
      },
    },
    {
      element: '[data-tour="post-image-text"]',
      popover: {
        title: 'edit the text on the image',
        description:
          'click any text on the image to rewrite it, drag it to move it, resize it, or × it away. add up to a few text boxes of your own — it’s a mini editor, no design tool needed.',
        side: 'right',
        align: 'center',
        showButtons: TOUR_BUTTONS,
      },
    },
    {
      element: '[data-tour="post-image-actions"]',
      popover: {
        title: 'copy or download the image',
        description: 'take the finished image to your clipboard, or download it to post from your phone.',
        side: 'left',
        align: 'end',
        showButtons: TOUR_BUTTONS,
      },
    },
    {
      element: '[data-tour="save-button"]',
      popover: {
        title: 'save the good ones',
        description: 'tap the heart to save a post — it lands in my posts, ready whenever you want it.',
        side: 'bottom',
        align: 'start',
        showButtons: TOUR_BUTTONS,
      },
    },
    {
      // Real by now, not hypothetical: the connections chapter ran BEFORE the
      // tool, so the accounts this posts to are already linked.
      element: '[data-tour="social-post-button"]',
      popover: {
        title: 'post it directly',
        description:
          'hit this to push a post straight to the accounts you connected earlier — no copy-paste, no scheduling tool.',
        side: 'bottom',
        align: 'end',
        showButtons: TOUR_BUTTONS,
      },
    },
    {
      element: '[data-tour="tone-picker"]',
      popover: {
        title: 'set the vibe',
        description:
          'set the vibe per platform here — same idea, different voice for linkedin vs twitter.',
        side: 'left',
        align: 'start',
        showButtons: TOUR_BUTTONS,
      },
    },
    {
      element: '[data-tour="sidebar-history"]',
      onHighlightStarted: () => {
        if (!openMobileMenuForTour()) return;
        setTimeout(() => requestTourReflow(), 60);
      },
      popover: {
        title: 'nothing gets lost',
        description:
          'every batch you generate is kept here in your history — search it, reopen an old run, or pull a post you didn’t save at the time.',
        side: 'right',
        align: 'center',
        showButtons: TOUR_BUTTONS,
      },
    },
    {
      element: '[data-tour="feedback-button"]',
      popover: {
        title: 'tell us when it’s wrong',
        description:
          'a post missed the mark, or something felt off? hit this. it goes straight to us and it’s genuinely how zupa gets better.',
        side: 'bottom',
        align: 'end',
        showButtons: TOUR_BUTTONS,
      },
    },
    {
      // Hand off to the OUTPUT pages — which now have something in them, because
      // the user has just made and saved a post. (They used to be toured before
      // any generation existed, so they were empty rooms.)
      element: '[data-tour="user-menu"]',
      action: walkTo('/posts', 'open the menu → “My Posts”'),
      popover: {
        title: 'so where did it go?',
        description:
          'you’ve made your first posts. open this menu and pick “my posts” — let’s go and find them.',
        side: 'bottom',
        align: 'end',
        showButtons: TOUR_BUTTONS,
        onNextClick: () => handOff('deep_dive_myposts'),
      },
    },
  ];
}

// ===========================================================================
// Onboarding personas sub-tour — the personas stage of /onboarding (Phase 3b)
// ===========================================================================
//
// Fires on the `'personas'` onboarding sub-step once the post-analyze tour has
// finished. The persona cards are generated asynchronously, so the list /
// regenerate steps are dropped by the DOM guard if the tour happens to fire
// before generation completes — the centered welcome + Finish Setup steps
// always survive.

export const onboardingPersonasSteps: TourStep[] = [
  {
    popover: {
      title: 'meet your audience 🎯',
      description:
        "a persona is a snapshot of who you're trying to reach. zupa built these from your company so every post lands with the right people.",
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    element: '[data-tour="onboarding-personas-list"]',
    popover: {
      title: 'your personas',
      description:
        "tap 'more details' on any card to see what drives each audience — their motivations, frustrations, and what makes them engage.",
      side: 'top',
      align: 'center',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    element: '[data-tour="onboarding-personas-regenerate"]',
    popover: {
      title: 'not quite right?',
      description: 'click regenerate for a fresh set — or add your own later in workspace settings.',
      side: 'top',
      align: 'center',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    element: '[data-tour="onboarding-save-button"]',
    popover: {
      title: 'finish setup',
      description: "happy with these? hit finish setup and we'll meet you in the tool.",
      side: 'top',
      align: 'center',
      showButtons: TOUR_BUTTONS,
      nextBtnText: 'got it',
      // End of the onboarding personas sub-tour. endTour(false): reset state
      // WITHOUT persisting completion so the tool_intro tour still fires.
      onNextClick: () => {
        void useTourStore.getState().endTour(false);
      },
    },
  },
];

// ===========================================================================
// Deep dive — a chain of per-page sub-tours ("show me everything")
// ===========================================================================
//
// Each sub-tour's last step navigates to the next page and stores the next
// phase in `pendingPhase`; the destination page's useEffect auto-fires its own
// sub-tour. `chainTo()` performs that handoff. The final sub-tour
// (deep_dive_preferences) ends in the EndScreen modal instead.

/**
 * Canonical chain order for the deep-dive sub-tours. Single source of truth —
 * the normal flow uses the `onNextClick` handlers below; the fallback in
 * `TourProvider` (when resolveSteps comes back empty) reads this array to
 * advance to the next phase rather than dying silently. `route` is null for
 * the in-page Workspaces handoffs (no navigation needed). Order must match
 * the actual `chainTo` / `chainWorkspacesSubTour` calls in the step lists.
 */
export const DEEP_DIVE_CHAIN_ORDER: ReadonlyArray<{
  phase: Exclude<TourPhase, null>;
  /** Route to navigate to. `null` = same-page handoff (workspaces sub-tours). */
  route: string | null;
}> = [
  // SET UP the brand...
  { phase: 'deep_dive_workspaces_page', route: '/settings/workspaces' },
  { phase: 'deep_dive_workspaces_profile', route: null },
  { phase: 'deep_dive_workspaces_services', route: null },
  { phase: 'deep_dive_workspaces_voice', route: null },
  { phase: 'deep_dive_workspaces_personas', route: null },
  { phase: 'deep_dive_connections', route: '/settings/connections' },
  { phase: 'deep_dive_subscription', route: '/settings/subscription' },
  { phase: 'deep_dive_preferences', route: '/settings/preferences' },
  { phase: 'deep_dive_feed', route: '/feed' },
  // ...MAKE something (tool_intro -> generate -> tool_intro_post, on /tool)...
  { phase: 'tool_intro', route: '/tool' },
  // ...THEN show where it lands. These pages used to be toured BEFORE the user
  // had generated anything, so every one of them was an empty room: an empty
  // library, no collections, a dashboard reading zero. Now they arrive holding a
  // post they just made and saved, and each page has something real in it.
  { phase: 'deep_dive_myposts', route: '/posts' },
  { phase: 'deep_dive_collections', route: '/collections' },
  { phase: 'deep_dive_dashboard', route: '/dashboard' },
  { phase: 'deep_dive_case_studies', route: '/case-studies' },
];

/**
 * Chapter list for the "jump to a section" picker (Preferences page). Covers
 * every IN-APP chapter a user can start directly: the Tool loop, then the
 * deep-dive chain with human-readable labels. Onboarding-only phases are
 * excluded — `/onboarding` is gated to users who haven't onboarded yet, so a
 * chapter link there would be a dead end for anyone who can see this list.
 *
 * Routes are derived from `DEEP_DIVE_CHAIN_ORDER` (the single source of truth
 * for chain routing) rather than hardcoded, so this list can't drift out of
 * sync with it. The three Workspaces sub-tours (services/voice/personas) have
 * `route: null` there — they're same-page tab handoffs inside the Workspaces
 * edit modal — so they fall back to the Workspaces profile route, which is
 * the page they actually live on.
 */
const WORKSPACES_ROUTE = DEEP_DIVE_CHAIN_ORDER.find(
  (e) => e.phase === 'deep_dive_workspaces_page',
)!.route as string;

function chapterRoute(phase: Exclude<TourPhase, null>): string {
  return DEEP_DIVE_CHAIN_ORDER.find((e) => e.phase === phase)?.route ?? WORKSPACES_ROUTE;
}

// Listed in journey order — the same order the chain runs in, so the picker
// reads as a table of contents rather than an arbitrary menu. The Tool is last
// because it IS last: generation is the finale.
export const TOUR_CHAPTERS: { label: string; phase: TourPhase; route: string }[] = [
  { label: 'Workspaces, Brief & Autopilot', phase: 'deep_dive_workspaces_page', route: chapterRoute('deep_dive_workspaces_page') },
  { label: 'Workspace Profile', phase: 'deep_dive_workspaces_profile', route: chapterRoute('deep_dive_workspaces_profile') },
  { label: 'Services', phase: 'deep_dive_workspaces_services', route: chapterRoute('deep_dive_workspaces_services') },
  { label: 'Brand Voice', phase: 'deep_dive_workspaces_voice', route: chapterRoute('deep_dive_workspaces_voice') },
  { label: 'Personas', phase: 'deep_dive_workspaces_personas', route: chapterRoute('deep_dive_workspaces_personas') },
  { label: 'Connections', phase: 'deep_dive_connections', route: chapterRoute('deep_dive_connections') },
  { label: 'Subscription', phase: 'deep_dive_subscription', route: chapterRoute('deep_dive_subscription') },
  { label: 'Preferences', phase: 'deep_dive_preferences', route: chapterRoute('deep_dive_preferences') },
  { label: 'Feed', phase: 'deep_dive_feed', route: chapterRoute('deep_dive_feed') },
  { label: 'The Tool', phase: 'tool_intro', route: '/tool' },
  { label: 'My Posts', phase: 'deep_dive_myposts', route: chapterRoute('deep_dive_myposts') },
  { label: 'Collections', phase: 'deep_dive_collections', route: chapterRoute('deep_dive_collections') },
  { label: 'Dashboard', phase: 'deep_dive_dashboard', route: chapterRoute('deep_dive_dashboard') },
  { label: 'Case Studies', phase: 'deep_dive_case_studies', route: chapterRoute('deep_dive_case_studies') },
];

/**
 * NOTE: there is no longer a `chainTo` (teleport) helper — EVERY hop in the
 * journey is now a real click the user makes themselves, via `walkTo` + `handOff`.
 * The three transitions that used to teleport are gone: the workspace edit modal
 * is closed by the user before they use the UserMenu; the Dashboard now renders a
 * UserMenu (it was the one logged-in page without one); and Subscription and
 * Case Studies were added to the UserMenu, so no hop has to rely on the
 * desktop-only settings sidebar.
 *
 * TourProvider still keeps DEEP_DIVE_CHAIN_ORDER for its zero-step fallback, which
 * navigates on the tour's behalf only when a chapter fails to resolve at all.
 */

/**
 * End the current sub-tour and arm the next one — WITHOUT navigating, because
 * the user just walked there themselves by clicking the control the step
 * spotlighted. The destination page's trigger picks up `pendingPhase` and fires.
 * Always the partner of a `walkTo` action.
 */
function handOff(nextPhase: TourPhase): void {
  const tour = useTourStore.getState();
  void tour.endTour(false);
  tour.setPendingPhase(nextPhase);
}

/**
 * End the current Workspaces sub-tour and hand off to the next sub-tour of the
 * SAME page. Unlike `chainTo`, this performs no navigation — all four
 * Workspaces sub-tours run inside the workspace edit modal on the one page.
 * The Workspaces page's trigger effect picks up `pendingPhase`, clicks the
 * matching tab, and fires the next sub-tour.
 */
function chainWorkspacesSubTour(nextPhase: TourPhase): void {
  const tour = useTourStore.getState();
  void tour.endTour(false);
  tour.setPendingPhase(nextPhase);
}

/**
 * Jump to the step in THIS phase that spotlights `selector` — used by a fork to
 * skip a chapter the user just said they don't want.
 *
 * Resolved by target at click time, not by a hardcoded index: `resolveSteps`
 * drops steps whose target is missing, so indices shift depending on what is on
 * the page. A literal index here would silently land on the wrong step the first
 * time someone's account lacked one of the optional cards.
 *
 * If the target isn't found (it was filtered out), fall through to the next step
 * rather than stranding the user — a fork must never be able to dead-end.
 */
function jumpToStep(selector: string): void {
  const tour = useTourStore.getState();
  const i = tour.currentSteps.findIndex((s) => (s.target ?? s.element) === selector);
  if (i >= 0) tour.goToStep(i);
  else tour.nextStep();
}

/**
 * Workspaces deep-dive — FOUR sub-tours, one per tab of the workspace edit
 * modal: Profile / Services / Voice / Personas. The Workspaces page clicks the
 * right tab BEFORE starting each sub-tour, so that tab's content is in the DOM
 * when the guard runs — which is why this is four phases and not one.
 *
 * ONE BEAT PER TAB, deliberately. The onboarding wizard just walked the user
 * through every one of these fields, so a second field-by-field pass would be
 * the tour repeating itself at its longest leg. The job here is different and
 * smaller: show them WHERE what they already entered now lives, and that they
 * can change it. Each beat spotlights one representative control on its tab —
 * never a whole-panel container, because the edit modal scrolls internally and
 * a section ring would drift off-screen (see the header notes on the onboarding
 * confirm card, same trap).
 */

/**
 * Workspaces PAGE chapter — runs BEFORE the edit modal is opened.
 *
 * Content Brief, Autopilot and "New Workspace" all live on the page itself, so
 * once the edit modal is up they're behind it and unreachable. They therefore
 * get their own chapter first; only then does the profile sub-tour open the
 * modal. (Started by `useDeepDiveTrigger` on the Workspaces page — it needs no
 * modal, unlike the four tab sub-tours below.)
 */
export const deepDiveWorkspacesPageSteps: TourStep[] = [
  {
    popover: {
      title: 'your workspaces 🏢',
      description:
        'a workspace is one brand. everything zupa knows about a company — its voice, services, audience — is scoped to one of these.',
      showButtons: TOUR_BUTTONS,
    },
  },
  // ---- ASK FIRST: do they even want a second brand? ------------------------
  // This used to be a mandatory hands-on gate: the user could not advance until
  // they opened the "New Workspace" modal, and then could not advance until they
  // CANCELLED out of it. Most people run one company, so we were making them mime
  // creating a second one they did not want, and the tour read as if the app were
  // telling them to. Ask instead, and skip the chapter for the people it is not
  // for. The button is still there whenever they need it.
  {
    element: '[data-tour="workspaces-new-button"]',
    popover: {
      title: 'one company, or more than one?',
      description:
        "a workspace is one brand — its own voice, services, personas and posts, nothing bleeding between them. most people only ever need the one. if you write for several companies or clients, each gets its own.",
      side: 'bottom',
      align: 'end',
      showButtons: TOUR_BUTTONS,
      choices: [
        {
          label: 'show me how to add another',
          onSelect: () => useTourStore.getState().nextStep(),
        },
        {
          label: 'just the one',
          subtle: true,
          onSelect: () => jumpToStep('[data-tour="workspaces-brief"]'),
        },
      ],
    },
  },
  // ---- "creating a new workspace", taught properly -------------------------
  // Only reached by someone who ASKED for it. The user walks into the create
  // modal, gets the fields explained, and CANCELS back out. They see exactly how
  // it's done without actually creating one — which matters, because
  // `handleCreate` activates the new empty workspace and opens its edit modal,
  // which would strand the tour on a blank brand.
  {
    element: '[data-tour="workspaces-new-button"]',
    action: {
      done: () => document.querySelector('[data-tour="workspaces-create-name"]') !== null,
      hint: 'click "New Workspace"',
    },
    popover: {
      title: 'adding a brand',
      description:
        'go on, hit "new workspace" and i\'ll walk you through the two fields. you won\'t actually create one — we\'ll back out at the end.',
      side: 'bottom',
      align: 'end',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    element: '[data-tour="workspaces-create-name"]',
    lateMount: true, // the modal isn't open when this phase starts
    popover: {
      title: 'name the brand',
      description: 'just the company or client name — this is how you\'ll tell your workspaces apart.',
      side: 'right',
      align: 'center',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    element: '[data-tour="workspaces-create-url"]',
    lateMount: true,
    popover: {
      title: 'and their website',
      description:
        'drop their url here and zupa reads the site to fill in the profile, voice and audience for you — the same way it did for your own company in setup. optional, but it saves you doing it by hand.',
      side: 'right',
      align: 'center',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    element: '[data-tour="workspaces-create-cancel"]',
    lateMount: true,
    action: {
      done: () => document.querySelector('[data-tour="workspaces-create-name"]') === null,
      hint: 'hit cancel — you can add one later',
    },
    popover: {
      title: 'that’s all it takes',
      description:
        'that\'s how you add a brand. you don\'t need a second one right now, so hit cancel and let\'s carry on — this button is here whenever you do.',
      side: 'top',
      align: 'end',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    element: '[data-tour="workspaces-brief"]',
    popover: {
      title: 'your content brief',
      description:
        'set the standing brief for this brand — the themes to keep hitting and the angles to avoid. zupa follows it on every post, so you stop repeating yourself in every prompt.',
      side: 'top',
      align: 'center',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    element: '[data-tour="workspaces-autopilot"]',
    popover: {
      title: 'autopilot ✈️',
      description:
        'switch autopilot on and zupa writes drafts for you on a schedule and drops them in your feed to approve — the posts appear without you asking. this is the hands-off way to run the whole thing.',
      side: 'top',
      align: 'center',
      showButtons: TOUR_BUTTONS,
      nextBtnText: 'next: inside a workspace',
      onNextClick: () => chainWorkspacesSubTour('deep_dive_workspaces_profile'),
    },
  },
];

/** Sub-tour 1/4 — the Profile (Company) tab. */
export const deepDiveWorkspacesProfileSteps: TourStep[] = [
  {
    // Reached BOTH ways: from the page chapter in the normal chain (an existing,
    // filled-in workspace), and straight after the user actually creates a NEW
    // one (an empty workspace whose edit modal just opened). The copy has to work
    // for both, so it talks about what this tab IS and how to fill it — not about
    // what's already in it.
    popover: {
      title: 'the brand’s brain 🧠',
      description:
        'a workspace holds everything zupa knows about one brand — profile, services, voice, audience. this is the foundation every post it writes is built on.',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    // THE answer to "how do I set up a workspace I just made": you don't type it
    // all in — you paste the website and let zupa read it, exactly as it did
    // during your own onboarding.
    element: '[data-tour="workspaces-analyze"]',
    popover: {
      title: 'don’t fill this in by hand',
      description:
        'paste the company’s website here and hit analyze — zupa reads the site and fills in the profile, the services, the brand voice and the visual brand dna (logo, colours, style) for you. it’s the same thing it did for your own company in setup.',
      side: 'bottom',
      align: 'start',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    element: '[data-tour="workspaces-company-name"]',
    popover: {
      title: 'or edit any of it yourself',
      description:
        'name, industry, description, audience, brand voice — all on this tab, and all editable. change a field and every future post follows it.',
      side: 'bottom',
      align: 'start',
      showButtons: TOUR_BUTTONS,
      nextBtnText: 'next: services',
      onNextClick: () => chainWorkspacesSubTour('deep_dive_workspaces_services'),
    },
  },
];

/** Sub-tour 2/4 — the Services tab. */
export const deepDiveWorkspacesServicesSteps: TourStep[] = [
  {
    element: '[data-tour="services-add-button"]',
    popover: {
      title: 'your services 💼',
      description:
        'the offerings you added in setup live on this tab — add, edit or remove them here as your business changes. tag a post to one and zupa writes about that exact thing, not your company in general.',
      side: 'bottom',
      align: 'center',
      showButtons: TOUR_BUTTONS,
      nextBtnText: 'next: voice',
      onNextClick: () => chainWorkspacesSubTour('deep_dive_workspaces_voice'),
    },
  },
];

/** Sub-tour 3/4 — the Voice tab. */
export const deepDiveWorkspacesVoiceSteps: TourStep[] = [
  {
    element: '[data-tour="voice-samples"]',
    popover: {
      title: 'how you sound 🎙️',
      description:
        'your tones and writing samples live here. this tab is the single biggest lever on quality — the more real writing you paste in, the more posts sound like you instead of ai. come back and add more any time.',
      side: 'top',
      align: 'center',
      showButtons: TOUR_BUTTONS,
      nextBtnText: 'next: personas',
      onNextClick: () => chainWorkspacesSubTour('deep_dive_workspaces_personas'),
    },
  },
];

/**
 * Sub-tour 4/4 — the Personas tab. Ends the workspaces leg.
 *
 * TELEPORTS to connections rather than gating a walk: the workspace edit modal
 * is open on top of the settings sidebar, so there is no reachable nav control
 * to point at. (The centered wrap-up step survives even if `personas-list`
 * doesn't render, so the chain always has something to hand off from.)
 */
export const deepDiveWorkspacesPersonasSteps: TourStep[] = [
  {
    element: '[data-tour="personas-list"]',
    popover: {
      title: 'your audience 🎯',
      description:
        'the personas zupa built for you live here — up to three. pick one when you write and the angle and tone shift to match them. add your own or regenerate any time.',
      side: 'top',
      align: 'center',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    // The user closes the modal themselves — the settings page (and the UserMenu
    // they need next) is underneath it.
    element: '[data-tour="workspaces-edit-close"]',
    action: {
      done: () => document.querySelector('[data-tour="workspaces-company-name"]') === null,
      hint: 'close the workspace',
    },
    popover: {
      title: "that's your foundation",
      description: "that's your brand set up. close this and let's hook up your social accounts.",
      side: 'top',
      align: 'end',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    element: '[data-tour="user-menu"]',
    action: walkTo('/settings/connections', 'open the menu → “Connections”'),
    popover: {
      title: 'next: connections',
      description: 'open this menu and pick “connections” — so the posts you write can go straight out.',
      side: 'bottom',
      align: 'end',
      showButtons: TOUR_BUTTONS,
      onNextClick: () => handOff('deep_dive_connections'),
    },
  },
];

/**
 * Connections deep-dive — linking social accounts.
 *
 * Runs BEFORE the tool on purpose. Connect the accounts now and the finale's
 * "post it directly" button is a real button they can press on a real post;
 * toured after generation (as it used to be) it was dead copy about a feature
 * they had no way to have set up yet.
 *
 * Ends with a gated WALK to My Posts via the UserMenu.
 */
export const deepDiveConnectionsSteps: TourStep[] = [
  {
    popover: {
      title: 'connect your socials 🔗',
      description:
        'link your accounts now and every post you write in a minute can go straight out from zupa — no copy-paste, no scheduling tool.',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    element: '[data-tour="connections-platforms"]',
    popover: {
      title: 'your platforms',
      description:
        "hit connect on each one you use — linkedin, instagram, facebook and x. a green badge means it's linked. you can do this later, but do it now and the rest of the tour gets more fun.",
      side: 'top',
      align: 'center',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    element: '[data-tour="user-menu"]',
    action: walkTo('/settings/subscription', 'open the menu → “Subscription”'),
    popover: {
      title: 'next: your plan',
      description: 'open the menu and pick “subscription” — a quick look at what you get.',
      side: 'bottom',
      align: 'end',
      showButtons: TOUR_BUTTONS,
      onNextClick: () => handOff('deep_dive_subscription'),
    },
  },
];

/**
 * My Posts deep-dive — the saved-posts library.
 *
 * EMPTY for a first-time user: they haven't generated anything yet (generation
 * is the finale). The copy is written for that — "this is where they'll land",
 * future tense — and the DOM guard drops the `myposts-card` step when there's
 * no card to point at. Same for Collections and Dashboard below.
 */
export const deepDiveMyPostsSteps: TourStep[] = [
  {
    popover: {
      title: 'there it is 📁',
      description:
        "every post you save lands here. this is your library — search it, re-use it, send it out, whenever you like.",
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    // Now this is a REAL card — the post they saved a minute ago.
    element: '[data-tour="myposts-card"]',
    popover: {
      title: 'the post you just saved',
      description:
        'each card carries the image, the copy, the platform it was built for, and a button to push it straight to your connected accounts.',
      side: 'top',
      align: 'center',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    element: '[data-tour="myposts-filters"]',
    popover: {
      title: 'find anything fast',
      description:
        'as posts pile up, search by text, filter by platform, or show only your favourites from right here.',
      side: 'bottom',
      align: 'center',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    element: '[data-tour="user-menu"]',
    action: walkTo('/collections', 'open the menu → “Collections”'),
    popover: {
      title: 'next: collections',
      description: 'open the menu again and pick “collections” — how you keep all this organized.',
      side: 'bottom',
      align: 'end',
      showButtons: TOUR_BUTTONS,
      onNextClick: () => handOff('deep_dive_collections'),
    },
  },
];

/** Collections deep-dive — grouping posts into folders. Empty for a new user. */
export const deepDiveCollectionsSteps: TourStep[] = [
  {
    popover: {
      title: 'collections 🗂️',
      description:
        'collections are folders for your posts — one per campaign, client, or topic. now that you’re making posts, this is how you keep them straight.',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    element: '[data-tour="collections-new-button"]',
    popover: {
      title: 'create a collection',
      description: 'click here to spin up a folder, then drop posts into it from the my posts page.',
      side: 'bottom',
      align: 'end',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    // Dropped by the DOM guard for a new user — no collections yet.
    element: '[data-tour="collections-card"]',
    popover: {
      title: 'a collection',
      description: 'click any collection to open it and see just the posts inside.',
      side: 'bottom',
      align: 'center',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    element: '[data-tour="user-menu"]',
    action: walkTo('/dashboard', 'open the menu → “Dashboard”'),
    popover: {
      title: 'next: your dashboard',
      description: 'one more from this menu — pick “dashboard”.',
      side: 'bottom',
      align: 'end',
      showButtons: TOUR_BUTTONS,
      onNextClick: () => handOff('deep_dive_dashboard'),
    },
  },
];

/**
 * Dashboard deep-dive — the impact / stats view. Zeroed out for a new user.
 *
 * TELEPORTS onward: the Dashboard page does not render the UserMenu (it only has
 * a back button), so there is no nav control to gate a walk on.
 */
export const deepDiveDashboardSteps: TourStep[] = [
  {
    popover: {
      title: 'your dashboard 📊',
      description:
        "the bird's-eye view — and it's already counting the posts you just made.",
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    element: '[data-tour="dashboard-stats"]',
    popover: {
      title: 'your impact',
      description:
        'your real return, tracked as you go — hours saved, money saved, posts and images created. it climbs every time you write.',
      side: 'bottom',
      align: 'center',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    element: '[data-tour="dashboard-milestone"]',
    popover: {
      title: 'milestones and streaks',
      description: 'your progress and the next milestone to hit, plus a streak for consecutive days creating 🔥',
      side: 'bottom',
      align: 'center',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    element: '[data-tour="dashboard-calendar"]',
    popover: {
      title: 'marketing calendar',
      description:
        'this one is useful today — upcoming dates and events worth posting about, so you never miss a moment.',
      side: 'top',
      align: 'center',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    // A real click now: the Dashboard finally renders the UserMenu (it was the
    // one logged-in page without it, which is why this hop used to teleport).
    element: '[data-tour="user-menu"]',
    action: walkTo('/case-studies', 'open the menu → “Case Studies”'),
    popover: {
      title: 'one last thing',
      description:
        'there’s a second engine in zupa you haven’t seen. open the menu and pick “case studies”.',
      side: 'bottom',
      align: 'end',
      showButtons: TOUR_BUTTONS,
      onNextClick: () => handOff('deep_dive_case_studies'),
    },
  },
];

/**
 * Case Studies deep-dive — the long-form engine, and the LAST chapter.
 *
 * Explains only. We never make the user write a case study: it's a long
 * generation about a real client engagement they'd have to invent on the spot,
 * and it runs on a different (Claude) pipeline. Show them what it is, where the
 * inputs go, and leave them to it.
 */
export const deepDiveCaseStudiesSteps: TourStep[] = [
  {
    popover: {
      title: 'case studies 📕',
      description:
        "zupa's other engine. the tool writes short social posts; this turns one real client win into a long-form case study and a blog — the kind of proof that closes deals.",
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    element: '[data-tour="case-study-new"]',
    popover: {
      title: 'start one when you have a win',
      description:
        'click here when you’ve got a client result worth writing up. you don’t need to do it now.',
      side: 'right',
      align: 'center',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    element: '[data-tour="case-study-intro"]',
    popover: {
      title: 'you bring the engagement',
      description:
        'you give it the client, the problem and the result. zupa writes it up in your brand voice, with your keywords worked in — no blank page.',
      side: 'bottom',
      align: 'start',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    element: '[data-tour="case-study-generate"]',
    popover: {
      title: 'and it writes the whole thing',
      description:
        'one click and you get a polished case study plus a blog post out of it. it takes longer than a social post — it’s writing something much bigger.',
      side: 'top',
      align: 'end',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    // THE END OF THE WHOLE JOURNEY.
    popover: {
      title: "and that's zupa 🎉",
      description:
        'an idea in, three posts out, saved and sent — you ran the whole loop yourself. the feed will have drafts waiting for you tomorrow. go build something.',
      showButtons: TOUR_BUTTONS,
      nextBtnText: 'finish',
      onNextClick: () => {
        const tour = useTourStore.getState();
        void tour.endTour(true);
        tour.openEndScreen();
      },
    },
  },
];

/** Subscription deep-dive — plan, usage and billing. */
export const deepDiveSubscriptionSteps: TourStep[] = [
  {
    element: '[data-tour="subscription-current"]',
    popover: {
      title: 'your plan 💳',
      description: "check here — the tier you're on, your trial or renewal date, and where to cancel.",
      side: 'bottom',
      align: 'center',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    element: '[data-tour="subscription-usage"]',
    popover: {
      title: "this month's usage",
      description: 'track your generations, ai images and workspaces against your limits here.',
      side: 'bottom',
      align: 'center',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    element: '[data-tour="subscription-plans"]',
    popover: {
      title: 'compare plans',
      description: 'upgrade here for more generations, images and workspaces — or switch plans any time.',
      side: 'top',
      align: 'center',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    // A real click, like every other hop. This used to teleport because the only
    // Subscription→Preferences link was the settings sidebar, which is
    // `hidden lg:block` — gating a click on it would have trapped mobile users.
    // The UserMenu is on every page at every breakpoint, so it's the right rail.
    element: '[data-tour="user-menu"]',
    action: walkTo('/settings/preferences', 'open the menu → “Preferences”'),
    popover: {
      title: 'next: your defaults',
      description: 'last settings stop — open the menu and pick “preferences”. then the good part.',
      side: 'bottom',
      align: 'end',
      showButtons: TOUR_BUTTONS,
      onNextClick: () => handOff('deep_dive_preferences'),
    },
  },
];

/**
 * Preferences deep-dive. Sits immediately before the Feed and the tool on
 * purpose: default tone and default platforms PRE-FILL the generation form, so
 * setting them here means the tool opens already configured when they get to it.
 *
 * Ends with a gated WALK to the Feed via the UserMenu.
 */
export const deepDivePreferencesSteps: TourStep[] = [
  {
    popover: {
      title: 'your defaults ⚙️',
      description:
        'set these once and zupa remembers them — the tool will open already configured when we get there in a minute.',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    element: '[data-tour="preferences-tone"]',
    popover: {
      title: 'default tone',
      description: 'pick a default tone — every new generation starts with it pre-selected.',
      side: 'bottom',
      align: 'center',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    element: '[data-tour="preferences-platforms"]',
    popover: {
      title: 'default platforms',
      description: 'pre-select the platforms you post to most, so the tool opens ready to go.',
      side: 'bottom',
      align: 'center',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    element: '[data-tour="preferences-notifications"]',
    popover: {
      title: 'email notifications',
      description: 'toggle which emails zupa sends you here.',
      side: 'top',
      align: 'center',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    element: '[data-tour="show-tutorial-again"]',
    popover: {
      title: 'replay the tour',
      description: 'click here any time to run through all of this again from the start.',
      side: 'top',
      align: 'center',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    element: '[data-tour="user-menu"]',
    action: walkTo('/feed', 'open the menu → “Feed”'),
    popover: {
      title: 'next: your feed',
      description:
        "settings done. open the menu and pick “feed” — that's where you'll actually start most days.",
      side: 'bottom',
      align: 'end',
      showButtons: TOUR_BUTTONS,
      onNextClick: () => handOff('deep_dive_feed'),
    },
  },
];

/**
 * Feed deep-dive — the daily front door, and the last stop before the tool.
 *
 * A brand-new user's feed is EMPTY (autopilot hasn't run for them yet), so the
 * drip / card steps are dropped by the DOM guard and the empty state carries the
 * chapter. That's fine: the point of this chapter is the PROMISE — zupa brings
 * you drafts, you approve, you're done — which is worth making over an empty
 * page.
 *
 * Its last step is the nicest walk in the tour: Feed's own "write your own"
 * button is the door into the finale, and clicking it teaches the two modes at
 * once (feed = zupa's ideas, tool = yours).
 */
export const deepDiveFeedSteps: TourStep[] = [
  {
    popover: {
      title: 'your feed 📬',
      description:
        "this is where you'll land most days. zupa reads your company, watches your industry, and drops ready-to-approve drafts here — you don't have to think of anything.",
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    // New user → this is what's actually on screen.
    element: '[data-tour="feed-empty"]',
    popover: {
      title: 'nothing yet — that’s normal',
      description:
        'your first drafts show up once zupa has had a run at your company. from then on there’s something waiting here each morning.',
      side: 'top',
      align: 'center',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    // Both of these are dropped by the DOM guard for a brand-new user.
    element: '[data-tour="feed-drip"]',
    popover: {
      title: 'a quick question',
      description:
        'answer these when they appear — each one teaches zupa something true about your business that it can use in a post.',
      side: 'top',
      align: 'center',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    element: '[data-tour="feed-card"]',
    popover: {
      title: 'approve and it’s done',
      description:
        'open a draft, tweak the wording if you want, approve — and it’s ready to go out. skip the ones that miss.',
      side: 'top',
      align: 'center',
      showButtons: TOUR_BUTTONS,
    },
  },
  {
    element: '[data-tour="feed-write-your-own"]',
    action: walkTo('/tool', 'hit “write your own”'),
    popover: {
      title: 'but you have your own idea',
      description:
        "the feed is zupa's ideas. this button is for yours — and that's the last thing to show you. hit it, and let's write your first post.",
      side: 'bottom',
      align: 'end',
      showButtons: TOUR_BUTTONS,
      onNextClick: () => handOff('tool_intro'),
    },
  },
];
